import { Component, computed, inject, input, output } from '@angular/core';
import { DocumentFile, DocumentNavItem } from '../../../../api/app-types/document.app';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CarouselElement, NavCarousel } from '../../../../core/components/nav-carousel/nav-carousel';
import { mapDocumentCategoryToKey } from '../../../../shared/helpers/documents.func';
import { MatTooltip } from '@angular/material/tooltip';
import { LvrButton } from '../../../../core/components/lvr-button/lvr-button';

@Component({
	selector: 'lvr-document-editor',
	imports: [TranslocoPipe, NavCarousel, MatTooltip, LvrButton],
	templateUrl: './document-editor.html',
	styleUrl: './document-editor.scss',
})
export class LvrDocumentEditor {
	private readonly sanitizer = inject(DomSanitizer);
	private readonly transloco = inject(TranslocoService);

	public readonly document = input.required<DocumentFile>();
	public readonly backLink = input.required<string>();
	public readonly documents = input.required<DocumentNavItem[]>();
	public readonly canDeleteByPermission = input<boolean>(true);
	public readonly isDownloading = input<boolean>(false);

	public readonly navigateToDocument = output<string>();
	public readonly deleteClicked = output<void>();
	public readonly downloadClicked = output<void>();

	public readonly isPdf = computed(() => this.document().mimeType === 'application/pdf');
	public readonly isImage = computed(() => this.document().mimeType.startsWith('image/'));
	public readonly canPreview = computed(() => this.isPdf() || this.isImage());
	public readonly canBeDeleted = computed(() => !this.currentItem()?.isFrozen);
	public readonly canDeleteDocument = computed(() => this.canBeDeleted() && this.canDeleteByPermission());
	public readonly safeUrl = computed<SafeResourceUrl | null>(() => {
		const location = this.document().location;
		if (!location.startsWith('https://')) {
			return null;
		}
		return this.sanitizer.bypassSecurityTrustResourceUrl(location);
	});

	public readonly breadcrumb = computed(() => {
		const doc = this.documents()?.[0];
		return doc
			? `${this.transloco.translate(mapDocumentCategoryToKey(doc.category))} / ${doc.entryName || '-'}`
			: '- / -';
	});

	public readonly carouselElements = computed(() =>
		this.documents().map((x) => ({
			id: x.file.id,
			label: x.file.name || '-',
		})),
	);
	public readonly currentItem = computed(() => {
		const id = this.document().id;
		return this.documents().find((x) => x.file.id === id);
	});
	public readonly currentIndex = computed(() => {
		const id = this.document().id;
		return this.documents().findIndex((x) => x.file.id === id);
	});

	public onNavigate(event: { index: number; element: CarouselElement }) {
		this.navigateToDocument.emit(event.element.id);
	}

	public getDeleteTooltip() {
		if (!this.canDeleteByPermission()) return 'base.button.edit_disabled';
		if (!this.canBeDeleted()) return 'client.documents.cannot_be_deleted';
		return '';
	}

	public download() {
		this.downloadClicked.emit();
	}

	public delete() {
		this.deleteClicked.emit();
	}
}
